import { Box, Chip, Typography } from "@mui/material";
import { useLocaleState, useTranslate } from "react-admin";

import { INTEGRATION_CREDENTIALS_NOT_CONFIGURED } from "../../constants/integrationErrors";
import {
  DocumentationSyncFieldsFragment,
  DocumentationSyncSkipReason,
  DocumentationSyncStatus as SyncStatus,
} from "../../generated/graphql";
import { formatDateTimeInline } from "../../lib/dateUtils";

type ChipColor = "success" | "error" | "warning" | "default";

const STATUS: Record<SyncStatus, { labelKey: string; color: ChipColor }> = {
  [SyncStatus.Sent]: { labelKey: "documentationSync.status.sent", color: "success" },
  [SyncStatus.Failed]: { labelKey: "documentationSync.status.failed", color: "error" },
  [SyncStatus.Pending]: { labelKey: "documentationSync.status.pending", color: "default" },
  [SyncStatus.Skipped]: { labelKey: "documentationSync.status.skipped", color: "warning" },
};

const SKIP_REASON_KEYS: Record<DocumentationSyncSkipReason, string> = {
  [DocumentationSyncSkipReason.NotIntegrated]: "documentationSync.skipReason.notIntegrated",
  [DocumentationSyncSkipReason.MissingResidentExternalId]: "documentationSync.skipReason.missingResidentExternalId",
  [DocumentationSyncSkipReason.MissingAuthorExternalId]: "documentationSync.skipReason.missingAuthorExternalId",
  [DocumentationSyncSkipReason.NoContent]: "documentationSync.skipReason.noContent",
};

const ERROR_KEYS = new Map<string, string>([
  [INTEGRATION_CREDENTIALS_NOT_CONFIGURED, "documentationSync.error.credentialsNotConfigured"],
  // Generic database lookup errors do not identify the missing data.
  ["Not found", "documentationSync.error.requiredDataNotFound"],
]);

/**
 * Documentation for residents that no provider manages is never exported, which is
 * expected rather than a problem, so those exports are not shown.
 */
export const visibleDocumentationSyncs = (
  syncs: DocumentationSyncFieldsFragment[] | null | undefined,
  seniorId?: string
): DocumentationSyncFieldsFragment[] =>
  (syncs ?? []).filter(
    sync =>
      !(sync.status === SyncStatus.Skipped && sync.skipReason === DocumentationSyncSkipReason.NotIntegrated) &&
      (!seniorId || sync.seniorId === seniorId)
  );

type DocumentationSyncStatusProps = {
  syncs?: DocumentationSyncFieldsFragment[] | null;
  /** Prefix for each export, e.g. the resident's name. */
  getLabel?: (sync: DocumentationSyncFieldsFragment) => string | undefined;
};

/**
 * Whether documentation reached the resident's care-documentation provider:
 * sent (with time), pending, failed (with the provider error) or skipped (with the reason).
 */
export const DocumentationSyncStatus = ({ syncs, getLabel }: DocumentationSyncStatusProps) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const shown = visibleDocumentationSyncs(syncs);
  if (shown.length === 0) return null;

  const errorDetail = (error: string | null | undefined): string => {
    if (!error) return translate("documentationSync.unknownError");
    const translationKey = ERROR_KEYS.get(error);
    return translationKey ? translate(translationKey) : error;
  };

  const detail = (sync: DocumentationSyncFieldsFragment): string => {
    switch (sync.status) {
      case SyncStatus.Sent:
        return [
          sync.provider && translate(`Procedure.fields.sourceSystem.${sync.provider.toLowerCase()}`),
          formatDateTimeInline(sync.sentAt, locale),
        ]
          .filter(Boolean)
          .join(" · ");
      case SyncStatus.Failed:
        return translate("documentationSync.failedDetail", {
          smart_count: sync.attempts,
          error: errorDetail(sync.lastError),
          // When the provider answered; the report may have arrived later.
          date: formatDateTimeInline(sync.outcomeAt ?? sync.updatedAt, locale),
        });
      case SyncStatus.Skipped:
        return sync.skipReason ? translate(SKIP_REASON_KEYS[sync.skipReason]) : "";
      default:
        return translate("documentationSync.pendingDetail");
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 0.5, mt: 0.5 }} data-testid="documentation-sync-status">
      {shown.map(sync => {
        const { labelKey, color } = STATUS[sync.status];
        const label = getLabel?.(sync);
        return (
          <Box key={sync.id} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip size="small" color={color} label={translate(labelKey)} />
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
              {label ? `${label} · ${detail(sync)}` : detail(sync)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
